// Pure streaming helper — no React dependencies.

export interface LocalMsg {
  id: string;
  role: "user" | "ai" | "file";
  text: string;
  ts: number;
  fileName?: string;
  fileType?: string;
  imgSrc?: string;
}

export interface LocalFileCtx {
  fileName: string;
  fileType: string;
  extractedText: string;
}

export function cleanMarkdown(text: string): string {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/^#+\s+/gm, "")
    .replace(/^\|[-: |]+\|$/gm, "")
    .replace(/\|/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export interface StreamChatResult {
  display: string | null;
  error: string | null;
  fieldUpdates?: string;
}

/**
 * Streams a chat completion from /api/chat.
 * Calls onChunk with the accumulated text after each SSE chunk.
 * Returns the final cleaned display string, or an error message.
 * Returns { display: null, error: null } if the request was aborted.
 */
export async function streamChat(
  userText: string,
  messages: LocalMsg[],
  chatModel: string,
  fileCtx: LocalFileCtx | null,
  signal: AbortSignal,
  onChunk: (fullText: string) => void,
): Promise<StreamChatResult> {
  const history = messages
    .filter(m => m.role !== "file")
    .slice(-10)
    .map(m => ({ role: m.role === "ai" ? "assistant" : "user", content: m.text }));
  history.push({ role: "user", content: userText });

  // Phase 3C item 2.4: the server now resolves a BYOK key server-side from
  // a stored connection (matched to chatModel's provider), scoped to
  // whichever real user the Authorization header identifies -- the client
  // no longer holds or transmits a raw API key. /api/chat remains callable
  // without a token (guest chat on non-gated models still works exactly as
  // before); a token just additionally unlocks BYOK-connection resolution
  // for a real, authenticated user. See
  // connections_routes.ts's resolveByokFromConnection.
  const lang = localStorage.getItem("studio_language") ?? "en";
  const authToken = localStorage.getItem("studio_token") ?? "";

  let res: Response;
  try {
    res = await fetch("/api/chat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      // FIX (Phase 3C item 2.4, found while touching this call site): this
      // previously sent the field as `chatModel`, but server.ts's /api/chat
      // handler reads `payload.model` -- Tier 0's explicit model selection
      // has never actually taken effect from this caller, silently falling
      // through to Tier 2-4 region defaults regardless of the picked model.
      body: JSON.stringify({ messages: history, lang, model: chatModel, fileContext: fileCtx }),
      signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") return { display: null, error: null };
    return { display: null, error: "Network error — please try again" };
  }

  if (!res.ok || !res.body) {
    return { display: null, error: "AI unavailable — add an API key in Account settings" };
  }

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "", full = "", streamError: string | null = null;

  try {
    outer: while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const lines = buf.split("\n"); buf = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6);
        if (data === "[DONE]") continue;
        try {
          const parsed = JSON.parse(data) as {
            type?: string; text?: string; error?: string; message?: string;
            choices?: Array<{ delta?: { content?: string } }>;
          };
          if (parsed.type === "error" || parsed.error) {
            window.dispatchEvent(new CustomEvent("studio:degradedMode", {
              detail: { message: parsed.message ?? parsed.error },
            }));
          }
          if (parsed.error) { streamError = parsed.error; break outer; }
          const delta = parsed.type === "chunk"
            ? (parsed.text ?? "")
            : (parsed.choices?.[0]?.delta?.content ?? "");
          if (delta) { full += delta; onChunk(full); }
        } catch { /* skip malformed SSE frame */ }
      }
    }
  } catch (err) {
    if ((err as Error).name === "AbortError") return { display: null, error: null };
    return { display: null, error: "Network error — please try again" };
  }

  if (streamError) return { display: null, error: streamError };

  const fieldUpdateMatch = full.match(/<FIELD_UPDATE>([\s\S]*?)<\/FIELD_UPDATE>/);
  const fieldUpdates = fieldUpdateMatch ? fieldUpdateMatch[1] : undefined;
  const cleaned = full
    ? cleanMarkdown(full.replace(/<FIELD_UPDATE>[\s\S]*?<\/FIELD_UPDATE>/g, "").trim())
    : "";
  return { display: cleaned || null, error: null, fieldUpdates };
}
